import React, { useCallback, useRef, ChangeEvent } from 'react'
import clsx from 'clsx'
import {
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  FormControl,
  Chip,
  styled,
  OutlinedInput,
} from '@mui/material'
import {
  FieldTitle,
  useInput,
  useChoicesContext,
  useChoices,
  RaRecord,
  useSupportCreateSuggestion,
  Labeled,
  LinearProgress,
  InputHelperText,
  SelectArrayInputClasses,
  sanitizeInputRestProps,
} from 'react-admin'
import { SelectArrayInputProps } from './type'

const PREFIX = 'RaSelectArrayInput'

const StyledFormControl = styled(FormControl, {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
  minWidth: theme.spacing(20),
  [`& .${SelectArrayInputClasses.chips}`]: {
    display: 'flex',
    flexWrap: 'wrap',
  },

  [`& .${SelectArrayInputClasses.chip}`]: {
    marginTop: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
}))

export const SelectArrayInput = (props: SelectArrayInputProps): JSX.Element => {
  const {
    choices: choicesProp,
    className,
    create,
    createLabel,
    createValue,
    disableValue,
    format,
    helperText,
    label,
    isFetching: isFetchingProp,
    isLoading: isLoadingProp,
    margin,
    onBlur,
    onChange,
    onCreate,
    optionText,
    optionValue,
    parse,
    resource: resourceProp,
    source: sourceProp,
    translateChoice,
    validate,
    variant,
    SelectInputProps,
    ...rest
  } = props

  const inputLabel = useRef(null)
  const { allChoices, isLoading, source, resource } = useChoicesContext({
    choices: choicesProp,
    isLoading: isLoadingProp,
    isFetching: isFetchingProp,
    resource: resourceProp,
    source: sourceProp,
  })

  const { getChoiceText, getChoiceValue, getDisableValue } = useChoices({
    optionText,
    optionValue,
    disableValue,
    translateChoice,
  })

  const {
    field,
    isRequired,
    fieldState: { error, invalid, isTouched },
    formState: { isSubmitted },
  } = useInput({
    format,
    onBlur,
    onChange,
    parse,
    resource,
    source,
    validate,
    ...rest,
  })

  const handleChange = useCallback(
    (eventOrChoice: ChangeEvent<HTMLInputElement> | RaRecord) => {
      // We might receive an event from the mui component
      // In this case, it will be the choice id
      if (eventOrChoice?.target) {
        field.onChange(eventOrChoice)
      } else {
        // Or we might receive a choice directly, for instance a newly created one
        field.onChange([...(field.value || []), getChoiceValue(eventOrChoice)])
      }
    },
    [field, getChoiceValue],
  )

  const {
    getCreateItem,
    handleChange: handleChangeWithCreateSupport,
    createElement,
  } = useSupportCreateSuggestion({
    create,
    createLabel,
    createValue,
    handleChange,
    onCreate,
    optionText,
  })

  const createItem = create || onCreate ? getCreateItem() : null
  const finalChoices = create || onCreate ? [...(allChoices || []), createItem] : allChoices || []

  const renderMenuItemOption = useCallback(
    (choice) =>
      !!createItem && choice?.id === createItem.id && typeof optionText === 'function'
        ? createItem.name
        : getChoiceText(choice),
    [createItem, getChoiceText, optionText],
  )

  const renderMenuItem = useCallback(
    (choice) => {
      return choice ? (
        <MenuItem
          key={getChoiceValue(choice)}
          value={getChoiceValue(choice)}
          disabled={getDisableValue(choice)}
        >
          {renderMenuItemOption(!!createItem && choice?.id === createItem.id ? createItem : choice)}
        </MenuItem>
      ) : null
    },
    [getChoiceValue, getDisableValue, renderMenuItemOption, createItem],
  )

  const handleDelete = useCallback(
    (targetId) => (): void => {
      const updatedChip = field.value.filter((id) => id !== targetId)
      field.onChange(updatedChip.length > 0 ? updatedChip : [])
    },
    [field],
  )

  if (isLoading) {
    return (
      <Labeled
        label={label}
        source={source}
        resource={resource}
        className={clsx('ra-input', `ra-input-${source}`, className)}
        isRequired={isRequired}
      >
        <LinearProgress />
      </Labeled>
    )
  }

  return (
    <>
      <StyledFormControl
        // focused={field.value && field.value?.length > 0}
        margin={margin}
        className={clsx('ra-input', `ra-input-${source}`, className)}
        error={(isTouched || isSubmitted) && invalid}
        variant={variant}
        {...sanitizeInputRestProps(rest)}
      >
        <InputLabel
          ref={inputLabel}
          id={`${label}-outlined-label`}
          error={(isTouched || isSubmitted) && invalid}
        >
          <FieldTitle label={label} source={source} resource={resource} isRequired={isRequired} />
        </InputLabel>
        <Select
          autoWidth
          labelId={`${label}-outlined-label`}
          multiple
          error={(isTouched || isSubmitted) && invalid}
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
          renderValue={(selected: any[]) => (
            <div className={SelectArrayInputClasses.chips}>
              {selected
                .map((item) => (allChoices || []).find((choice) => getChoiceValue(choice) === item))
                .filter((item) => !!item)
                .map((item) => (
                  <Chip
                    key={getChoiceValue(item)}
                    label={renderMenuItemOption(item)}
                    className={SelectArrayInputClasses.chip}
                    size='small'
                    onMouseDown={(e): void => {
                      e.stopPropagation()
                    }}
                    onDelete={handleDelete(item.id)}
                  />
                ))}
            </div>
          )}
          data-testid='selectArray'
          {...field}
          onChange={handleChangeWithCreateSupport}
          value={field.value || []}
          input={<OutlinedInput id={`${label}-outlined-label`} label={label} />}
          {...SelectInputProps}
        >
          {finalChoices.map(renderMenuItem)}
        </Select>
        <FormHelperText error={isTouched && !!error}>
          <InputHelperText
            touched={isTouched || isSubmitted}
            error={error?.message}
            helperText={helperText}
          />
        </FormHelperText>
      </StyledFormControl>
      {createElement}
    </>
  )
}

SelectArrayInput.defaultProps = {
  options: {},
  optionText: 'name',
  optionValue: 'id',
  disableValue: 'disabled',
  translateChoice: true,
  SelectInputProps: { size: 'medium' },
}
